/* =========================================================
   Import from Excel.

   Download a per-entity .xlsx template, fill it, upload it
   back. The prototype faked the parse; here the file is really
   read with SheetJS, its columns are matched against the
   entity's field schema, and each row is validated before the
   preview — so the valid/error counts are true.
   ========================================================= */
import React, { useState } from 'react';
import type { FieldSchema } from '@/types';
import { ic, sized } from '@/components/Icons';
import { Section } from '@/components/ui';
import { useI18n } from '@/i18n';
import { apiSource, ds } from '@/data';
import {
  assignmentSchema, instructorSchema, orgSchema, siteSchema, validateImportField,
} from '@/lib/fields';
import { IMPORT_CHUNK, importRows, type ImportRowError } from '@/lib/api';
import { COUNTRIES } from '@/data/catalogs';
import { downloadSheets, parseSpreadsheet } from '@/lib/xlsx';
import { useAppStore } from '@/store/useAppStore';

type Entity = 'orgs' | 'sites' | 'instructors' | 'assignments';

const SCHEMA: Record<Entity, FieldSchema> = {
  orgs: orgSchema,
  sites: siteSchema,
  instructors: instructorSchema,
  assignments: assignmentSchema,
};

const TPL: Record<Entity, { sheet: string; file: string }> = {
  orgs: { sheet: 'Organizaciones', file: 'plantilla_organizaciones' },
  sites: { sheet: 'Sites', file: 'plantilla_sites' },
  instructors: { sheet: 'Instructores', file: 'plantilla_instructores' },
  assignments: { sheet: 'Afiliaciones', file: 'plantilla_afiliaciones' },
};

const dedupe = (a: string[]) => a.filter((v, i) => a.indexOf(v) === i);

interface PreviewRow extends Record<string, string | undefined> {
  __error?: string;
  /** Número de fila EN EL ARCHIVO, contando la cabecera. Es el que hay
   *  que decirle a quien va a corregir el Excel: «la 412» solo sirve si
   *  coincide con lo que ve al abrirlo. */
  __fila?: string;
}

interface Preview {
  headers: string[];
  /** Headers in the file that match no schema field. */
  unmapped: string[];
  /** TODAS las filas del archivo.
   *
   *  Estuvo recortado a las primeras 50, y con novecientos instructores
   *  eso significaba que un error en la fila 300 era invisible: los
   *  contadores decían «12 con error» y la tabla no enseñaba ninguno.
   *  No había forma de saber cuáles arreglar. */
  rows: PreviewRow[];
  /** Todas las filas sin error de formato — esto es lo que se envía. */
  sendable: Record<string, string>[];
  valid: number;
  errors: number;
}

/** Las tres vistas de la revisión. */
type Ficha = 'todas' | 'errores' | 'ayuda';

/* ---------------- qué significa cada error ----------------

   Un mensaje como «Código postal: formato inválido» dice QUÉ pasa pero no
   QUÉ HACER, y quien rellena el Excel casi nunca es quien conoce las
   reglas. Este catálogo existe para cerrar esa distancia: por cada tipo de
   error, en qué consiste, un ejemplo de lo que lo provoca y cómo se
   arregla.

   Cubre los dos orígenes, porque desde fuera se ven igual: los que
   detecta el navegador al leer el archivo (formato de un campo) y los que
   solo aparecen al mandarlo al servidor (identificadores repetidos,
   referencias que no existen). */

interface TipoError {
  titulo_es: string; titulo_en: string;
  que_es: string; que_es_en: string;
  ejemplo: string;
  arreglo: string; arreglo_en: string;
}

const CATALOGO_ERRORES: TipoError[] = [
  {
    titulo_es: 'Correo con formato inválido', titulo_en: 'Invalid email format',
    que_es: 'El texto no tiene forma de correo electrónico.',
    que_es_en: 'The text is not shaped like an email address.',
    ejemplo: 'juan.perez@empresa  ·  juan perez@empresa.com  ·  juan@@empresa.com',
    arreglo: 'Tiene que llevar una arroba y un punto después: nombre@empresa.com. '
      + 'Revisa que no haya espacios ni dos arrobas.',
    arreglo_en: 'It needs one @ and a dot after it: name@company.com. Check for spaces or double @.',
  },
  {
    titulo_es: 'Código postal inválido', titulo_en: 'Invalid postal code',
    que_es: 'El código postal tiene caracteres que no se esperan.',
    que_es_en: 'The postal code has unexpected characters.',
    ejemplo: 'CP 1426 (con letras)  ·  s/n  ·  ---',
    arreglo: 'Deja solo el código, sin la palabra «CP» ni guiones sueltos. '
      + 'Si de verdad no hay código postal, deja la celda VACÍA: un campo vacío se acepta.',
    arreglo_en: 'Leave just the code. If there is genuinely none, leave the cell EMPTY — blanks are accepted.',
  },
  {
    titulo_es: 'Teléfono inválido', titulo_en: 'Invalid phone',
    que_es: 'El número tiene símbolos que no se reconocen.',
    que_es_en: 'The number has unrecognised symbols.',
    ejemplo: 'tel. 5551234  ·  555-1234 / 555-5678  ·  N/A',
    arreglo: 'Un solo número, con dígitos, espacios, paréntesis o un «+» delante. '
      + 'Si hay dos teléfonos, deja uno. Si no hay, deja la celda vacía en vez de escribir «N/A».',
    arreglo_en: 'One number only. If there are two, keep one. If there is none, leave it empty instead of «N/A».',
  },
  {
    titulo_es: 'Falta el identificador', titulo_en: 'Missing identifier',
    que_es: 'La fila no trae el código que identifica el registro (OrgId, SiteId o InstructorId).',
    que_es_en: 'The row has no identifier (OrgId, SiteId or InstructorId).',
    ejemplo: 'La primera columna en blanco',
    arreglo: 'Sin ese código no se sabe si la fila crea un registro nuevo o actualiza uno que ya existe. '
      + 'Si la fila sobra, bórrala entera del Excel en vez de dejarla a medias.',
    arreglo_en: 'Without it we cannot tell a new record from an update. If the row is not needed, delete it.',
  },
  {
    titulo_es: 'Identificador repetido', titulo_en: 'Duplicate identifier',
    que_es: 'El mismo código aparece en dos filas del archivo.',
    que_es_en: 'The same code appears twice in the file.',
    ejemplo: 'ORG128 en la fila 15 y otra vez en la 16',
    arreglo: 'Compara las dos filas. Si son idénticas, borra una. Si tienen datos distintos, '
      + 'decide cuál es la buena y borra la otra: el sistema no puede elegir por ti.',
    arreglo_en: 'Compare both rows. If identical, delete one. If they differ, decide which is right.',
  },
  {
    titulo_es: 'La organización no existe', titulo_en: 'Organization does not exist',
    que_es: 'Un site dice pertenecer a una organización que no está cargada en el portal.',
    que_es_en: 'A site points to an organization that is not loaded in the portal.',
    ejemplo: 'Un site con OrgId = ORG128, cuando ORG128 no se ha cargado',
    arreglo: 'Carga PRIMERO las organizaciones y después los sites. Si ya lo hiciste, '
      + 'esa organización falta en el archivo de organizaciones: pídesela a quien lo prepara.',
    arreglo_en: 'Load organizations FIRST, then sites. If you did, that organization is missing from the file.',
  },
  {
    titulo_es: 'El país no está en el catálogo', titulo_en: 'Country not in the catalogue',
    que_es: 'El país escrito no se reconoce.',
    que_es_en: 'The country written is not recognised.',
    ejemplo: 'Wakanda  ·  Honduras (si aún no está dado de alta)',
    arreglo: 'Se acepta el código de dos letras (PE), el nombre en castellano (Perú) y en inglés (Peru). '
      + 'Mira la hoja «Países» de la plantilla. Si el país es real y no está en la lista, avísanos para añadirlo.',
    arreglo_en: 'Code (PE), Spanish name (Perú) or English name (Peru) all work. See the «Países» sheet.',
  },
  {
    titulo_es: 'Se esperaba un número', titulo_en: 'A number was expected',
    que_es: 'Un campo numérico trae texto.',
    que_es_en: 'A numeric field contains text.',
    ejemplo: 'Workstations = «doce»  ·  YearJoined = «2008 aprox»',
    arreglo: 'Escribe solo el número, sin letras ni comentarios. Si no se sabe, deja la celda vacía.',
    arreglo_en: 'Digits only, no words. If unknown, leave it empty.',
  },
  {
    titulo_es: 'Columna que no existe', titulo_en: 'Unknown column',
    que_es: 'El archivo trae una columna que el portal no conoce.',
    que_es_en: 'The file has a column the portal does not know.',
    ejemplo: 'Una columna «Observaciones» añadida a mano',
    arreglo: 'Bórrala del Excel, o vuelve a bajar la plantilla y copia tus datos dentro. '
      + 'Importante: NO cambies los nombres de la primera fila, son los que el portal lee.',
    arreglo_en: 'Delete it, or download the template again and paste your data inside. Do not rename row 1.',
  },
  {
    titulo_es: 'Falta un dato obligatorio', titulo_en: 'A required value is missing',
    que_es: 'Un registro NUEVO no trae un dato que es imprescindible.',
    que_es_en: 'A NEW record is missing something mandatory.',
    ejemplo: 'Una organización sin OrgName  ·  un site sin SiteName',
    arreglo: 'Complétalo. Solo se exige al dar de alta: al actualizar un registro que ya existe, '
      + 'los campos que no menciones se quedan como están.',
    arreglo_en: 'Fill it in. Only required when creating; updates keep untouched fields as they are.',
  },
];

/** Resultado de comprobar o aplicar, ya sumado entre tandas. */
interface Outcome {
  dryRun: boolean;
  applied: boolean;
  created: number;
  updated: number;
  unchanged: number;
  errors: ImportRowError[];
}

export function ExcelImportCard() {
  const { lang, t } = useI18n();
  const addToast = useAppStore((s) => s.addToast);
  const [entity, setEntity] = useState<Entity>('sites');
  const [tplEntity, setTplEntity] = useState<Entity>('sites');
  /** Bajar la plantilla con lo que ya hay dentro, para corregir en vez de
   *  dar de alta. Es lo que hace falta para repasar el padrón entero. */
  const [conDatos, setConDatos] = useState(true);
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<Preview | null>(null);
  /** Qué ficha de la revisión se está mirando. Se abre en «errores»
   *  cuando los hay: con novecientas filas, lo primero que uno necesita
   *  no es la lista entera, es la docena que está mal. */
  const [ficha, setFicha] = useState<Ficha>('todas');
  const [outcome, setOutcome] = useState<Outcome | null>(null);
  const [busy, setBusy] = useState(false);

  // Aplicar exige haber comprobado antes: la validación del navegador no
  // sabe si el país existe ni si la organización de un site está creada.
  const comprobado = outcome?.dryRun === true && outcome.errors.length === 0;

  /** Las filas que hay hoy en el padrón, en el orden de las columnas.
   *
   *  La plantilla salía siempre vacía, y con ella solo se pueden CREAR
   *  registros: para corregir trescientas organizaciones había que
   *  teclear a mano el identificador de cada una. Y la exportación de
   *  Reportes no servía de origen, porque encabeza con nombres humanos
   *  («Nombre de la organización») y el importador espera claves de campo
   *  («OrgName»): el viaje de ida y vuelta estaba roto.
   *
   *  Con los datos dentro, el ciclo es el que espera cualquiera: bajar,
   *  corregir en Excel, volver a subir. */
  function filasActuales(ent: Entity, keys: string[]): string[][] {
    const registros: Record<string, unknown>[] =
      ent === 'orgs' ? (ds.listOrgs() as unknown as Record<string, unknown>[])
      : ent === 'sites' ? (ds.listSites() as unknown as Record<string, unknown>[])
      : ent === 'assignments' ? (ds.listAssignments() as unknown as Record<string, unknown>[])
      : (ds.listInstructors() as unknown as Record<string, unknown>[]);
    return registros.map((r) => keys.map((k) => {
      const v = r[k];
      return v == null || v === false ? '' : v === true ? 'true' : String(v);
    }));
  }

  function downloadTemplate() {
    const cfg = TPL[tplEntity];
    const schema = SCHEMA[tplEntity];
    const keys = dedupe(schema.map((f) => f.key));
    // row 1: machine headers (field keys) · row 2: human labels (hint)
    const labelRow = keys.map((k) => {
      const f = schema.find((x) => x.key === k);
      return f ? (lang === 'es' ? f.label_es : f.label_en) : k;
    });
    const datos = conDatos ? filasActuales(tplEntity, keys) : [];

    /* Hojas de apoyo: los identificadores y códigos que hay que escribir,
       para copiar y pegar en vez de teclear de memoria.

       No es un adorno. El primer padrón real llegó con el país escrito
       completo —«Peru» donde la base espera «PE»— y eso, con una
       importación que es todo o nada, tumbó las 137 organizaciones de
       golpe. Ahora el servidor entiende las dos formas, pero enseñar el
       catálogo evita la duda antes de que ocurra.

       Van DESPUÉS de la hoja de datos a propósito: el importador lee
       siempre la primera, así que estas viajan sin estorbar. */
    const apoyo: { name: string; rows: (string | number)[][] }[] = [];
    const es = lang === 'es';

    if (tplEntity === 'orgs' || tplEntity === 'sites') {
      apoyo.push({
        name: es ? 'Países' : 'Countries',
        rows: [
          [es ? 'Código (esto es lo que se escribe)' : 'Code (write this)', es ? 'País' : 'Country'],
          ...COUNTRIES.map((c) => [c.code, es ? c.name_es : c.name_en]),
        ],
      });
    }
    if (tplEntity === 'assignments') {
      apoyo.push({
        name: 'Sites',
        rows: [
          ['SiteId', es ? 'Nombre del site' : 'Site name', es ? 'Organización' : 'Organization'],
          ...ds.listSites().map((s) => [s.SiteId, s.SiteName, s.OrgName || s.OrgId]),
        ],
      });
    }

    downloadSheets(cfg.file, [
      { name: cfg.sheet, rows: [keys, labelRow, ...datos] },
      ...apoyo,
    ]);
    addToast({
      kind: 'template',
      title: lang === 'es' ? 'Plantilla descargada' : 'Template downloaded',
      body: conDatos
        ? `${cfg.file}.xlsx · ${datos.length} ${lang === 'es' ? 'filas con los datos de hoy' : 'rows with today\u2019s data'}`
        : `${cfg.file}.xlsx · ${lang === 'es' ? 'vacía, para dar de alta' : 'blank, for new records'}`,
    });
  }

  /** Se lleva la revisión a un archivo.
   *
   *  Corregir un padrón no se hace delante de la pantalla: se hace en
   *  Excel, a ratos, y a veces lo hace otra persona que ni siquiera tiene
   *  acceso al portal. Sin esto había que ir copiando a mano los números
   *  de fila de la tabla, con novecientas delante.
   *
   *  Tres hojas: las filas malas con TODAS sus columnas —para poder
   *  arreglarlas ahí mismo—, el resumen, y el catálogo de errores para
   *  quien reciba el archivo sin haber visto la pantalla. */
  function descargarInforme() {
    if (!preview) return;
    const es = lang === 'es';
    const malas = preview.rows.filter((r) => r.__error);

    const hojaErrores = [
      [es ? 'Fila en el Excel' : 'Row in the file', es ? 'Problema' : 'Problem', ...preview.headers],
      ...malas.map((r) => [r.__fila ?? '', r.__error ?? '', ...preview.headers.map((h) => r[h] ?? '')]),
    ];

    const hojaResumen = [
      [es ? 'Revisión de la importación' : 'Import review'],
      [],
      [es ? 'Archivo' : 'File', file?.name ?? ''],
      [es ? 'Tipo de datos' : 'Data type', entity],
      [es ? 'Fecha de la revisión' : 'Reviewed on', new Date().toLocaleString(es ? 'es' : 'en')],
      [],
      [es ? 'Filas en el archivo' : 'Rows in the file', String(preview.rows.length)],
      [es ? 'Filas correctas' : 'Valid rows', String(preview.valid)],
      [es ? 'Filas con error' : 'Rows with errors', String(preview.errors)],
      [],
      [es ? 'Columnas reconocidas' : 'Recognised columns', preview.headers.join(', ')],
      ...(preview.unmapped.length
        ? [[es ? 'Columnas ignoradas' : 'Ignored columns', preview.unmapped.join(', ')]]
        : []),
    ];

    const hojaAyuda = [
      [es ? 'Tipo de error' : 'Error type', es ? 'Qué significa' : 'What it means',
        es ? 'Ejemplo' : 'Example', es ? 'Cómo se arregla' : 'How to fix it'],
      ...CATALOGO_ERRORES.map((c) => [
        es ? c.titulo_es : c.titulo_en,
        es ? c.que_es : c.que_es_en,
        c.ejemplo,
        es ? c.arreglo : c.arreglo_en,
      ]),
    ];

    downloadSheets(
      `revision_${entity}_${new Date().toISOString().slice(0, 10)}`,
      [
        { name: es ? 'Errores' : 'Errors', rows: hojaErrores },
        { name: es ? 'Resumen' : 'Summary', rows: hojaResumen },
        { name: es ? 'Qué significan' : 'Error guide', rows: hojaAyuda },
      ],
    );

    addToast({
      kind: 'template',
      title: es ? 'Informe descargado' : 'Report downloaded',
      body: malas.length
        ? `${malas.length} ${es ? 'filas con error, con todas sus columnas' : 'rows with errors, with all their columns'}`
        : (es ? 'Sin errores: el informe lleva solo el resumen.' : 'No errors: the report has the summary only.'),
    });
  }

  async function onFile(e: React.ChangeEvent<HTMLInputElement>) {
    const f = e.target.files?.[0];
    e.target.value = '';
    if (!f) return;
    setFile(f);
    setBusy(true);
    try {
      const parsed = await parseSpreadsheet(f);
      const schema = SCHEMA[entity];
      const known = new Set(schema.map((x) => x.key));

      // El número de fila se calcula ANTES de filtrar nada: la cabecera es
      // la 1, así que la primera fila de datos es la 2. Si se contara
      // después, cada fila saltada correría la numeración y mandaríamos a
      // corregir la fila equivocada.
      const dataRows = parsed.rows
        .map((r, i) => ({ r, fila: i + 2 }))
        // the template's second row repeats the human labels — skip it
        .filter(({ r }) => {
          const first = parsed.headers[0];
          return !(first && r[first] && schema.some((s) => s.label_es === r[first] || s.label_en === r[first]));
        });

      const headers = parsed.headers.filter((h) => known.has(h));
      const unmapped = parsed.headers.filter((h) => h && !known.has(h));

      // Se valida el archivo entero, no solo lo que cabe en la tabla: los
      // recuentos de válidas y con error tienen que ser los de verdad, y
      // lo que se envía son todas las filas buenas.
      const todas: PreviewRow[] = dataRows.map(({ r, fila }) => {
        const row: PreviewRow = { __fila: String(fila) };
        headers.forEach((h) => {
          row[h] = r[h] ?? '';
        });
        // first failing field decides the row's error label
        for (const fld of schema) {
          if (!headers.includes(fld.key)) continue;
          const err = validateImportField(fld, row[fld.key]);
          if (err) {
            const label = lang === 'es' ? fld.label_es : fld.label_en;
            row.__error = `${label}: ${t('field.' + err)}`;
            break;
          }
        }
        return row;
      });

      const rows = todas;
      const sendable = todas
        .filter((r) => !r.__error)
        .map((r) => {
          const limpio: Record<string, string> = {};
          headers.forEach((h) => { limpio[h] = r[h] ?? ''; });
          return limpio;
        });

      if (!headers.length) {
        addToast({
          kind: 'err',
          title: lang === 'es' ? 'No se reconocieron columnas' : 'No columns recognised',
          body: lang === 'es'
            ? 'Usa la plantilla de la entidad: la primera fila debe traer las claves de campo.'
            : 'Use the entity template: the first row must carry the field keys.',
        });
      }

      setOutcome(null);
      const conError = todas.length - sendable.length;
      // Si hay errores, se abre directamente en su ficha. Quien acaba de
      // subir novecientas filas y tiene doce mal no quiere empezar por la
      // lista entera.
      setFicha(conError > 0 ? 'errores' : 'todas');
      setPreview({
        headers,
        unmapped,
        rows,
        sendable,
        valid: sendable.length,
        errors: conError,
      });
    } catch {
      setPreview(null);
      addToast({
        kind: 'err',
        title: lang === 'es' ? 'No se pudo leer el archivo' : 'Could not read the file',
        body: lang === 'es' ? 'Comprueba que sea .xlsx o .csv válido.' : 'Check that it is a valid .xlsx or .csv.',
      });
    } finally {
      setBusy(false);
    }
  }

  /** Envía las filas en tandas y suma los resultados.
   *
   *  Cada tanda es una transacción: hasta 1000 filas —el caso normal de un
   *  maestro— es todo o nada. Por encima, una tanda posterior puede fallar
   *  con las anteriores ya aplicadas; por eso se comprueba antes. */
  async function enviar(dryRun: boolean) {
    if (!preview || !preview.sendable.length) return;
    setBusy(true);
    const total: Outcome = { dryRun, applied: false, created: 0, updated: 0, unchanged: 0, errors: [] };
    try {
      for (let i = 0; i < preview.sendable.length; i += IMPORT_CHUNK) {
        const tanda = preview.sendable.slice(i, i + IMPORT_CHUNK);
        const res = await importRows(entity, tanda, dryRun);
        if (!res.ok) {
          setOutcome(null);
          addToast({
            kind: 'err',
            title: dryRun
              ? (lang === 'es' ? 'La comprobación falló' : 'The check failed')
              : (lang === 'es' ? 'No se importó' : 'Not imported'),
            body: res.message,
          });
          return;
        }
        total.applied = total.applied || res.summary.applied;
        total.created += res.summary.created;
        total.updated += res.summary.updated;
        total.unchanged += res.summary.unchanged;
        // los números de fila son relativos a su tanda
        total.errors.push(...res.summary.errors.map((e) => ({ ...e, row: e.row + i })));
      }
      setOutcome(total);

      if (dryRun) {
        addToast({
          kind: total.errors.length ? 'err' : 'ok',
          title: total.errors.length
            ? (lang === 'es' ? 'Hay filas que no entrarían' : 'Some rows would not go in')
            : (lang === 'es' ? 'Comprobación correcta' : 'Check passed'),
          body: `${total.created} ${lang === 'es' ? 'altas' : 'new'} · ${total.updated} ${lang === 'es' ? 'cambios' : 'changed'} · ${total.unchanged} ${lang === 'es' ? 'sin cambio' : 'unchanged'}`,
        });
      } else {
        addToast({
          kind: 'import',
          title: lang === 'es' ? 'Importación aplicada' : 'Import applied',
          body: `${total.created} ${lang === 'es' ? 'altas' : 'created'} · ${total.updated} ${lang === 'es' ? 'actualizadas' : 'updated'}`,
        });
        setFile(null);
        setPreview(null);
        // Lo importado está en la base, pero el portal trabaja con la copia
        // que cargó al entrar: sin volver a leerla, el panel seguía
        // enseñando los datos de antes hasta recargar la página.
        try {
          await apiSource().hydrate();
          useAppStore.getState().bumpProgreso();
        } catch {
          /* si falla, se ve al recargar; el import ya está aplicado */
        }
      }
    } finally {
      setBusy(false);
    }
  }

  const entityOptions: { v: Entity; l: string }[] = [
    { v: 'orgs', l: lang === 'es' ? 'Organizaciones' : 'Organizations' },
    { v: 'sites', l: 'Sites' },
    { v: 'instructors', l: lang === 'es' ? 'Instructores' : 'Instructors' },
    { v: 'assignments', l: lang === 'es' ? 'Afiliaciones' : 'Assignments' },
  ];

  return (
    <Section
      title={t('admin.import')}
      tone="var(--brand-primary)"
      right={preview ? <span className="chip ok">{preview.valid} ✓ · {preview.errors} {lang === 'es' ? 'errores' : 'errors'}</span> : null}
    >
      <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 12 }}>
        {/* download template */}
        <div className="card" style={{ padding: 14, background: 'var(--surface-2)', border: '1px solid var(--line)' }}>
          <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontWeight: 700, fontSize: 13 }}>{lang === 'es' ? 'Plantilla de carga' : 'Upload template'}</div>
              <div style={{ fontSize: 12, color: 'var(--text-2)', marginTop: 2 }}>
                {lang === 'es'
                  ? 'Descarga el .xlsx de la entidad, corrígelo y vuelve a subirlo. Con «incluir los datos actuales» baja lo que ya hay, para repasarlo; sin marcar, baja vacía para dar de alta.'
                  : 'Download the entity .xlsx, correct it and upload it back. With “include current data” it brings what already exists, to review it; unchecked, it comes blank for new records.'}
              </div>
            </div>
            <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end', flexWrap: 'wrap' }}>
              <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-2)' }}>{lang === 'es' ? 'Entidad destino' : 'Target entity'}</span>
                <select
                  value={tplEntity}
                  onChange={(e) => setTplEntity(e.target.value as Entity)}
                  style={{ padding: '7px 10px', borderRadius: 6, border: '1px solid var(--line-2)', fontSize: 13, background: 'var(--surface)' }}
                >
                  {entityOptions.map((o) => (
                    <option key={o.v} value={o.v}>{o.l}</option>
                  ))}
                </select>
              </label>
              <label
                style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: 12.5, paddingBottom: 7, cursor: 'pointer' }}
                data-tip={lang === 'es'
                  ? 'Con los datos dentro puedes corregir lo que ya existe. Vacía, solo sirve para dar de alta registros nuevos.'
                  : 'With the data inside you can correct what already exists. Blank, it only serves to create new records.'}
              >
                <input type="checkbox" checked={conDatos} onChange={(e) => setConDatos(e.target.checked)} />
                {lang === 'es' ? 'Incluir los datos actuales' : 'Include current data'}
              </label>
              <button className="btn primary" onClick={downloadTemplate}>
                {ic.download} {lang === 'es' ? 'Descargar plantilla' : 'Download template'} (.xlsx)
              </button>
            </div>
          </div>
        </div>

        <div style={{ fontSize: 12, color: 'var(--text-2)' }}>{t('admin.import_help')}</div>

        <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
          <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-2)' }}>{t('admin.target_entity')}</span>
            <select
              value={entity}
              onChange={(e) => {
                setEntity(e.target.value as Entity);
                setPreview(null);
                setFile(null);
              }}
              style={{ padding: '7px 10px', borderRadius: 6, border: '1px solid var(--line-2)', fontSize: 13, background: 'var(--surface)' }}
            >
              {entityOptions.map((o) => (
                <option key={o.v} value={o.v}>{o.l}</option>
              ))}
            </select>
          </label>

          <label className="btn" style={{ cursor: 'pointer', marginTop: 14 }}>
            {ic.upload} {busy ? t('common.loading') : file ? file.name : t('admin.choose_file')}
            <input type="file" accept=".xlsx,.xls,.csv" onChange={onFile} style={{ display: 'none' }} />
          </label>
          <span style={{ fontSize: 11, color: 'var(--text-3)', marginTop: 14 }}>.xlsx · .csv</span>
        </div>

        {preview ? (
          <div className="card" style={{ overflow: 'hidden' }}>
            <div style={{ padding: '10px 14px', background: 'var(--surface-2)', borderBottom: '1px solid var(--line)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
              <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                <span style={{ color: 'var(--text-2)' }}>{ic.excel}</span>
                <div>
                  <div style={{ fontWeight: 700, fontSize: 13 }}>{t('admin.map_columns')}</div>
                  <div style={{ fontSize: 11, color: 'var(--text-3)', marginTop: 1 }}>
                    {preview.headers.length} {lang === 'es' ? 'columnas mapeadas' : 'columns mapped'} ·{' '}
                    {preview.valid + preview.errors}{' '}
                    {lang === 'es' ? 'filas en el archivo' : 'rows in the file'}
                    {preview.unmapped.length
                      ? ` · ${preview.unmapped.length} ${lang === 'es' ? 'sin mapear' : 'unmapped'}`
                      : ''}
                  </div>
                </div>
              </div>
              <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                <span className="chip ok">{preview.valid} {t('admin.rows_valid')}</span>
                <span className="chip err">{preview.errors} {t('admin.rows_errors')}</span>
              </div>
            </div>

            {preview.unmapped.length ? (
              <div style={{ padding: '8px 14px', fontSize: 11.5, color: 'var(--text-2)', background: 'var(--st-edit-l)', borderBottom: '1px solid var(--line)' }}>
                {sized(ic.warn, 12)}{' '}
                {lang === 'es' ? 'Columnas ignoradas (no existen en el esquema): ' : 'Ignored columns (not in the schema): '}
                <span className="mono">{preview.unmapped.join(', ')}</span>
              </div>
            ) : null}

            {/* Las tres fichas. La de errores lleva su cuenta en la
                pestaña: con novecientas filas, saber que hay doce mal sin
                tener que entrar es la mitad del trabajo. */}
            <div
              style={{
                display: 'flex', alignItems: 'center', gap: 4, padding: '0 10px',
                borderBottom: '1px solid var(--line)', background: 'var(--surface-2)',
                flexWrap: 'wrap',
              }}
            >
              {([
                { id: 'todas' as Ficha, txt: lang === 'es' ? 'Todas' : 'All', n: preview.rows.length, tono: 'neutro' },
                { id: 'errores' as Ficha, txt: lang === 'es' ? 'Errores' : 'Errors', n: preview.errors, tono: 'err' },
                { id: 'ayuda' as Ficha, txt: lang === 'es' ? 'Qué significan los errores' : 'What the errors mean', n: null, tono: 'neutro' },
              ]).map((f) => {
                const activa = ficha === f.id;
                return (
                  <button
                    key={f.id}
                    type="button"
                    onClick={() => setFicha(f.id)}
                    style={{
                      background: 'transparent', border: 'none', cursor: 'pointer',
                      padding: '10px 12px', fontSize: 12.5,
                      fontWeight: activa ? 700 : 500,
                      color: activa ? 'var(--text)' : 'var(--text-2)',
                      borderBottom: `2px solid ${activa ? 'var(--brand-primary)' : 'transparent'}`,
                      marginBottom: -1,
                      display: 'inline-flex', alignItems: 'center', gap: 6,
                    }}
                  >
                    {f.txt}
                    {f.n !== null ? (
                      <span
                        className={`chip ${f.tono === 'err' && f.n > 0 ? 'err' : ''}`}
                        style={{ fontSize: 11 }}
                      >
                        {f.n}
                      </span>
                    ) : null}
                  </button>
                );
              })}
              <button
                type="button"
                className="btn sm"
                onClick={descargarInforme}
                style={{ marginLeft: 'auto', marginBlock: 6 }}
              >
                {sized(ic.download, 13)} {lang === 'es' ? 'Descargar informe' : 'Download report'}
              </button>
            </div>

            {/* Contenedor con altura fija y su propio desplazamiento: las
                filas ya no se recortan a cincuenta, pero novecientas
                dentro del flujo de la página harían la pantalla
                inmanejable. La cabecera se queda fija al desplazar. */}
            {ficha === 'todas' ? (
              <div style={{ overflow: 'auto', maxHeight: 460 }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                  <thead style={{ position: 'sticky', top: 0, zIndex: 1, background: 'var(--surface)' }}>
                    <tr>
                      <th style={thStyle}>{lang === 'es' ? 'Fila' : 'Row'}</th>
                      {preview.headers.map((h) => (
                        <th key={h} style={{ ...thStyle, whiteSpace: 'nowrap' }}>{h}</th>
                      ))}
                      <th style={thStyle} />
                    </tr>
                  </thead>
                  <tbody>
                    {preview.rows.map((r, i) => (
                      <tr key={i} style={{ background: r.__error ? 'var(--st-req-l)' : 'transparent' }}>
                        <td className="mono" style={{ padding: '7px 12px', color: 'var(--text-3)', borderBottom: '1px solid var(--line)' }}>{r.__fila}</td>
                        {preview.headers.map((h) => (
                          <td
                            key={h}
                            style={{ padding: '7px 12px', borderBottom: '1px solid var(--line)', fontFamily: /Id|CSN|Code/.test(h) ? 'var(--mono)' : 'inherit' }}
                          >
                            {r[h] || <span style={{ color: 'var(--text-3)' }}>—</span>}
                          </td>
                        ))}
                        <td style={{ padding: '7px 12px', borderBottom: '1px solid var(--line)' }}>
                          {r.__error ? <span className="chip err">{ic.warn} {r.__error}</span> : <span className="chip ok">{ic.check} OK</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : null}

            {ficha === 'errores' ? (
              preview.errors === 0 ? (
                <div style={{ padding: 28, textAlign: 'center', color: 'var(--text-2)', fontSize: 13 }}>
                  <div style={{ color: 'var(--ok)', display: 'flex', justifyContent: 'center', marginBottom: 8 }}>
                    {sized(ic.check, 28)}
                  </div>
                  {lang === 'es'
                    ? 'Ninguna fila tiene errores de formato. Pulsa «Comprobar» para que el servidor revise lo que solo él puede ver: identificadores repetidos y referencias que no existen.'
                    : 'No formatting errors. Press «Check» so the server can review what only it can see: duplicate identifiers and missing references.'}
                </div>
              ) : (
                <div style={{ overflow: 'auto', maxHeight: 460 }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                    <thead style={{ position: 'sticky', top: 0, zIndex: 1, background: 'var(--surface)' }}>
                      <tr>
                        <th style={thStyle}>{lang === 'es' ? 'Fila' : 'Row'}</th>
                        <th style={{ ...thStyle, whiteSpace: 'nowrap' }}>
                          {lang === 'es' ? 'Identificador' : 'Identifier'}
                        </th>
                        <th style={thStyle}>{lang === 'es' ? 'Problema' : 'Problem'}</th>
                        <th style={thStyle}>{lang === 'es' ? 'Valor que lo provoca' : 'Offending value'}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {preview.rows.filter((r) => r.__error).map((r, i) => {
                        // El campo que falló va delante del «:» en el mensaje.
                        const campo = (r.__error ?? '').split(':')[0].trim();
                        const clave = SCHEMA[entity].find(
                          (f) => f.label_es === campo || f.label_en === campo,
                        )?.key;
                        return (
                          <tr key={i}>
                            <td className="mono" style={{ padding: '7px 12px', borderBottom: '1px solid var(--line)', color: 'var(--err)', fontWeight: 700 }}>
                              {r.__fila}
                            </td>
                            <td className="mono" style={{ padding: '7px 12px', borderBottom: '1px solid var(--line)' }}>
                              {r[preview.headers[0]] || '—'}
                            </td>
                            <td style={{ padding: '7px 12px', borderBottom: '1px solid var(--line)' }}>
                              <span className="chip err">{ic.warn} {r.__error}</span>
                            </td>
                            <td className="mono" style={{ padding: '7px 12px', borderBottom: '1px solid var(--line)', color: 'var(--text-2)' }}>
                              {(clave && r[clave]) || <span style={{ color: 'var(--text-3)' }}>(vacío)</span>}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )
            ) : null}

            {ficha === 'ayuda' ? (
              <div style={{ overflow: 'auto', maxHeight: 460, padding: '4px 14px 14px' }}>
                <p style={{ fontSize: 12.5, color: 'var(--text-2)', lineHeight: 1.55, margin: '12px 0 16px' }}>
                  {lang === 'es'
                    ? 'Estos son los motivos por los que el portal puede rechazar una fila. Los tres primeros los detecta el navegador al leer el archivo; los demás solo aparecen al pulsar «Comprobar», porque hace falta mirar lo que ya está cargado.'
                    : 'These are the reasons a row can be rejected. The first three are caught when reading the file; the rest only appear on «Check», because they need to look at what is already loaded.'}
                </p>
                {CATALOGO_ERRORES.map((c) => (
                  <div
                    key={c.titulo_es}
                    style={{
                      borderLeft: '3px solid var(--line-2)', paddingLeft: 12, marginBottom: 16,
                    }}
                  >
                    <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 3 }}>
                      {lang === 'es' ? c.titulo_es : c.titulo_en}
                    </div>
                    <div style={{ fontSize: 12.5, color: 'var(--text-2)', lineHeight: 1.5 }}>
                      {lang === 'es' ? c.que_es : c.que_es_en}
                    </div>
                    <div className="mono" style={{ fontSize: 11.5, color: 'var(--text-3)', margin: '5px 0', wordBreak: 'break-word' }}>
                      {lang === 'es' ? 'Ejemplo: ' : 'Example: '}{c.ejemplo}
                    </div>
                    <div style={{ fontSize: 12.5, lineHeight: 1.5 }}>
                      <strong>{lang === 'es' ? 'Cómo se arregla: ' : 'How to fix it: '}</strong>
                      <span style={{ color: 'var(--text-2)' }}>{lang === 'es' ? c.arreglo : c.arreglo_en}</span>
                    </div>
                  </div>
                ))}
              </div>
            ) : null}

            {outcome ? (
              <div
                style={{
                  padding: '10px 14px', borderTop: '1px solid var(--line)', fontSize: 12.5,
                  background: outcome.errors.length ? 'var(--st-req-l)' : 'var(--st-read-l)',
                  color: outcome.errors.length ? 'var(--st-req)' : 'var(--st-read)',
                }}
              >
                <div style={{ fontWeight: 700 }}>
                  {outcome.dryRun
                    ? (lang === 'es' ? 'Resultado de la comprobación' : 'Check result')
                    : (lang === 'es' ? 'Resultado' : 'Result')}
                </div>
                <div style={{ marginTop: 2 }}>
                  {outcome.created} {lang === 'es' ? 'fichas nuevas' : 'new records'} ·{' '}
                  {outcome.updated} {lang === 'es' ? 'con cambios' : 'changed'} ·{' '}
                  {outcome.unchanged} {lang === 'es' ? 'iguales que ahora' : 'already up to date'}
                </div>
                {outcome.errors.length ? (
                  <ul style={{ margin: '6px 0 0', paddingLeft: 18 }}>
                    {outcome.errors.slice(0, 8).map((e) => (
                      <li key={`${e.row}-${e.id}`}>
                        {lang === 'es' ? 'Fila' : 'Row'} {e.row}
                        {e.id ? ` (${e.id})` : ''}: {e.message}
                      </li>
                    ))}
                    {outcome.errors.length > 8 ? (
                      <li>… {outcome.errors.length - 8} {lang === 'es' ? 'más' : 'more'}</li>
                    ) : null}
                  </ul>
                ) : null}
              </div>
            ) : null}

            <div style={{ padding: '10px 14px', borderTop: '1px solid var(--line)', display: 'flex', justifyContent: 'flex-end', gap: 8, flexWrap: 'wrap' }}>
              <button className="btn" onClick={() => { setPreview(null); setFile(null); setOutcome(null); }}>
                {t('common.cancel')}
              </button>
              <button className="btn" onClick={() => void enviar(true)} disabled={busy || !preview.valid}>
                {ic.shield} {lang === 'es' ? 'Comprobar' : 'Check'} ({preview.valid})
              </button>
              <button
                className="btn primary"
                onClick={() => void enviar(false)}
                disabled={busy || !comprobado}
                data-tip={comprobado ? undefined : (lang === 'es' ? 'Comprueba primero.' : 'Run the check first.')}
              >
                {ic.check} {t('admin.apply')} ({preview.valid})
              </button>
            </div>
          </div>
        ) : (
          <div className="card" style={{ padding: 28, textAlign: 'center', border: '1px dashed var(--line-2)', background: 'var(--surface-2)' }}>
            <div style={{ color: 'var(--text-3)', display: 'flex', justifyContent: 'center', marginBottom: 8 }}>{sized(ic.excel, 32)}</div>
            <div style={{ fontSize: 13, color: 'var(--text-2)' }}>
              {lang === 'es' ? 'Selecciona un archivo .xlsx o .csv para previsualizar el mapeo.' : 'Pick an .xlsx or .csv file to preview the mapping.'}
            </div>
          </div>
        )}
      </div>
    </Section>
  );
}

const thStyle: React.CSSProperties = {
  padding: '8px 12px', textAlign: 'left', fontSize: 10, color: 'var(--text-3)',
  textTransform: 'uppercase', fontWeight: 700, letterSpacing: '.04em',
  background: 'var(--surface-2)', borderBottom: '1px solid var(--line)',
};
